import React, { useState, useEffect } from 'react';
import { useQueryClient, useMutation } from '@tanstack/react-query';

import { toastActions } from '../../../../components/uielements/toast/toast';
import { IntlMessages } from '../../../../helper/langHelper';
import AdminModal from '../../../../components/uielements/modal/adminModal';

import {
  FormWrapper,
  FormSection,
  FieldGroup,
  Label,
  ErrorText,
  ButtonContainer,
  PrimaryButton,
} from '../../users/modals/UserPromoteModal.styles';
import { addPdf, updatePdf } from '../../../../services/admin/pdfs';

const EditPdfModal = ({ onClose, isEdit = false, pdfId = null, initialTitle = '' }) => {
  const queryClient = useQueryClient();

  const [pdfTitle, setPdfTitle] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (isEdit && initialTitle) {
      setPdfTitle(initialTitle);
    }
  }, [isEdit, initialTitle]);

  const addMutation = useMutation({
    mutationFn: () => addPdf({ pdfTitle }),
    onSuccess: () => {
      toastActions.success(
        <IntlMessages id="toast.pdf.add.success" defaultMessage="PDF added successfully." />
      );
      queryClient.invalidateQueries({ queryKey: ['adminPdfs'] });
      onClose();
    },
    onError: (err) => {
      toastActions.error(
        <IntlMessages id="toast.pdf.add.error" defaultMessage="Failed to add PDF." />
      );
      setError(err?.message || 'Failed to add PDF. Please try again.');
    },
  });

  const updateMutation = useMutation({
    mutationFn: () => updatePdf({ pdfId, pdfTitle }),
    onSuccess: () => {
      toastActions.success(
        <IntlMessages id="toast.pdf.update.success" defaultMessage="PDF updated successfully." />
      );
      queryClient.invalidateQueries({ queryKey: ['adminPdfs'] });
      onClose();
    },
    onError: (err) => {
      toastActions.error(
        <IntlMessages id="toast.pdf.update.error" defaultMessage="Failed to update PDF." />
      );
      setError(err?.message || 'Failed to update PDF. Please try again.');
    },
  });

  const isLoading = addMutation.isPending || updateMutation.isPending;

  const validateForm = () => {
    if (!pdfTitle.trim()) {
      setError('Please enter a PDF title.');
      return false;
    }
    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) return;

    if (isEdit) {
      updateMutation.mutate();
    } else {
      addMutation.mutate();
    }
  };

  const handleClose = () => {
    setPdfTitle('');
    setError('');
    onClose();
  };

  const footer = (
    <ButtonContainer>
      <PrimaryButton type="submit" disabled={isLoading || !pdfTitle.trim()}>
        {isLoading ? (isEdit ? 'Updating…' : 'Adding…') : isEdit ? 'Update PDF' : 'Add PDF'}
      </PrimaryButton>
    </ButtonContainer>
  );

  return (
    <AdminModal
      isOpen={true}
      onClose={handleClose}
      title={isEdit ? 'Edit PDF' : 'Add PDF'}
      footer={footer}
      width="520px"
    >
      <FormWrapper onSubmit={handleSubmit}>
        {error && <ErrorText>{error}</ErrorText>}

        <FormSection>
          <FieldGroup>
            <Label>PDF Title</Label>
            <input
              type="text"
              value={pdfTitle}
              onChange={(e) => {
                setPdfTitle(e.target.value);
                if (error) setError('');
              }}
              placeholder="Enter PDF title..."
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '14px',
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
          </FieldGroup>
        </FormSection>
      </FormWrapper>
    </AdminModal>
  );
};

export default EditPdfModal;
